import {
  type TypedDocumentNode,
  type OperationVariables,
  type MutationUpdaterFunction,
  type DefaultContext,
  type ApolloCache,
  type Unmasked,
} from "@apollo/client"
import {
  type MutateResult,
  type UseQueryOptions,
  useMutation,
  useQuery,
} from "@vue/apollo-composable"
import { type MaybeRef, computed, reactive, ref, unref, toRef } from "vue"

import type { BaseEntity } from "@/types"
import { type ContextFilter, type Maybe, type MutationRoot, type QueryRoot } from "@/graphql/types"
import { handleMutationErrors } from "@/utils/composables/handleMutationErrors"
import { overrideValuesFromObject } from "@/utils/data"
import { splitLongId, type StrictEntityId } from "@/utils/entity"
import { useWhenResult } from "@/utils/misc"

function buildNonAdminContext<T extends OperationVariables>(
  id: StrictEntityId
): T & { context: ContextFilter } {
  const [cid, oid, iid] = splitLongId(id)
  //All of our list queries institutionContext are being referenced as 'context'
  const context = {
    customer: cid,
    organization: oid,
    institution: iid,
  }
  return {
    ...({} as T),
    context,
  }
}

export type EntityList<TEntity extends BaseEntity> = {
  __typename?: string
  items: TEntity[]
  page?: Maybe<number>
  total?: Maybe<number>
  limit?: Maybe<number>
}

export type BaseListResult<
  TEntity extends BaseEntity,
  TEntityKey extends keyof QueryRoot,
  TListEntities extends BaseEntity = TEntity,
> =
  | Record<TEntityKey, EntityList<TEntity>>
  | Partial<
      Record<Exclude<keyof QueryRoot, TEntityKey>, EntityList<Exclude<TListEntities, TEntity>>>
    >
  | undefined
// { [T in string]: EntityList<T extends TEntityKey ? TEntity : Exclude<TListEntities, TEntity>> }

export type BaseGetByIdResult<TEntity extends BaseEntity> =
  | Partial<Record<keyof QueryRoot, TEntity>>
  | undefined
export type BaseCreateResult<TEntity extends BaseEntity> =
  | Partial<Record<keyof MutationRoot, TEntity>>
  | undefined
export type BaseUpdateResult<TEntity extends BaseEntity> =
  | Partial<Record<keyof MutationRoot, TEntity>>
  | undefined
export type BaseRemoveResult = Partial<Record<keyof MutationRoot, number>> | undefined
export type BaseLinkResult = Partial<Record<keyof MutationRoot, number>> | undefined

export type MutationOptions = {
  context?: DefaultContext & {
    skipGlobalSuccessToast?: boolean
    skipGlobalErrorToast?: boolean
  }
}

export type GqlOperations<
  TEntity extends BaseEntity,
  TEntityListKey extends keyof QueryRoot,
  TListResult extends BaseListResult<TEntity, TEntityListKey, TListEntities>,
  TListVariables extends OperationVariables,
  TGetByIdResult extends BaseGetByIdResult<TEntity>,
  TGetByIdVariables extends OperationVariables,
  TCreateResult extends BaseCreateResult<TEntity>,
  TCreateVariables extends OperationVariables,
  TUpdateResult extends BaseUpdateResult<TEntity>,
  TUpdateVariables extends OperationVariables,
  TRemoveResult extends BaseRemoveResult,
  TRemoveVariables extends OperationVariables,
  TLinkResult extends BaseLinkResult = undefined,
  TLinkVariables extends OperationVariables = object,
  TListEntities extends BaseEntity = TEntity,
> = {
  readonly list: undefined extends TListResult
    ? undefined
    : MaybeRef<TypedDocumentNode<TListResult, TListVariables>>
  readonly getById: undefined extends TGetByIdResult
    ? undefined
    : TypedDocumentNode<TGetByIdResult, TGetByIdVariables>
  readonly create: undefined extends TCreateResult
    ? undefined
    : TypedDocumentNode<TCreateResult, TCreateVariables>
  readonly update: undefined extends TUpdateResult
    ? undefined
    : TypedDocumentNode<TUpdateResult, TUpdateVariables>
  readonly remove: undefined extends TRemoveResult
    ? undefined
    : TypedDocumentNode<TRemoveResult, TRemoveVariables>
  readonly link: undefined extends TLinkResult
    ? undefined
    : TypedDocumentNode<TLinkResult, TLinkVariables>
}

export type ResultMap<
  TEntity extends BaseEntity,
  TEntityListKey extends keyof QueryRoot,
  TListResult extends BaseListResult<TEntity, TEntityListKey, TListEntities>,
  TGetByIdResult extends BaseGetByIdResult<TEntity>,
  TCreateResult extends BaseCreateResult<TEntity>,
  TUpdateResult extends BaseUpdateResult<TEntity>,
  TRemoveResult extends BaseRemoveResult,
  TLinkResult extends BaseLinkResult = undefined,
  TListEntities extends BaseEntity = TEntity,
> = {
  getList: undefined extends TListResult ? undefined : (result: TListResult) => EntityList<TEntity>
  getById?: undefined extends TGetByIdResult ? undefined : (result: TGetByIdResult) => TEntity
  getCreated: undefined extends TCreateResult ? undefined : (result: TCreateResult) => TEntity
  getUpdated: undefined extends TUpdateResult ? undefined : (result: TUpdateResult) => TEntity
  getRemovedCount: undefined extends TRemoveResult ? undefined : (result: TRemoveResult) => number
  getLinkedCount: undefined extends TLinkResult ? undefined : (result: TLinkResult) => number
}

export type ListCacheReducer<
  TEntity extends BaseEntity,
  TEntityListKey extends keyof QueryRoot,
  TListResult extends BaseListResult<TEntity, TEntityListKey, TListEntities>,
  TResult,
  TVariables extends OperationVariables,
  TListEntities extends BaseEntity = TEntity,
> = (cachedQuery: TListResult, data: TResult, variables: TVariables) => TListResult

export type ListCacheReducerContext<
  TResult,
  TVariables extends OperationVariables = OperationVariables,
> = (data: TResult) => TVariables | undefined

export type RemovedIdsMapper<TRemoveVariables extends OperationVariables> = (
  variables: TRemoveVariables
) => string[]

export type GetRemoveReducer<TRemoveResult, TRemoveVariables extends OperationVariables> = (
  mapRemovedIds: RemovedIdsMapper<TRemoveVariables>
) => MutationUpdaterFunction<TRemoveResult, TRemoveVariables, DefaultContext, ApolloCache<unknown>>

export type ApiParams<
  TEntity extends BaseEntity,
  TEntityListKey extends keyof QueryRoot,
  TListResult extends BaseListResult<TEntity, TEntityListKey, TListEntities>,
  TListVariables extends OperationVariables,
  TGetByIdResult extends BaseGetByIdResult<TEntity>,
  TGetByIdVariables extends OperationVariables,
  TCreateResult extends BaseCreateResult<TEntity>,
  TCreateVariables extends OperationVariables,
  TUpdateResult extends BaseUpdateResult<TEntity>,
  TUpdateVariables extends OperationVariables,
  TRemoveResult extends BaseRemoveResult,
  TRemoveVariables extends OperationVariables,
  TLinkResult extends BaseLinkResult = undefined,
  TLinkVariables extends OperationVariables = object,
  TListEntities extends BaseEntity = TEntity,
> = {
  typename: string
  operations: GqlOperations<
    TEntity,
    TEntityListKey,
    TListResult,
    TListVariables,
    TGetByIdResult,
    TGetByIdVariables,
    TCreateResult,
    TCreateVariables,
    TUpdateResult,
    TUpdateVariables,
    TRemoveResult,
    TRemoveVariables,
    TLinkResult,
    TLinkVariables,
    TListEntities
  >
  resultMap: ResultMap<
    TEntity,
    TEntityListKey,
    TListResult,
    TGetByIdResult,
    TCreateResult,
    TUpdateResult,
    TRemoveResult,
    TLinkResult,
    TListEntities
  >
  mapRemovedIds: RemovedIdsMapper<TRemoveVariables>
  listQueryVariables: MaybeRef<TListVariables>
  listQueryOptions?: MaybeRef<UseQueryOptions<TListResult, TListVariables>>
  mutationVariables?: MaybeRef<OperationVariables>
  getByIdAdditionalVariables?: MaybeRef<OperationVariables>
  handleAdditionalCacheRemoveUpdates?: MutationUpdaterFunction<
    TRemoveResult,
    TRemoveVariables,
    DefaultContext,
    ApolloCache<unknown>
  > | null
  buildCtxFromResultId?: (id: StrictEntityId) => TListVariables
  beforeRefetch?: () => void | Promise<void>
}

export type Api<
  TEntity extends BaseEntity,
  TEntityListKey extends keyof QueryRoot,
  TListResult extends BaseListResult<TEntity, TEntityListKey, TListEntities>,
  TListVariables extends OperationVariables,
  TGetByIdResult extends BaseGetByIdResult<TEntity>,
  TGetByIdVariables extends OperationVariables,
  TCreateResult extends BaseCreateResult<TEntity>,
  TCreateVariables extends OperationVariables,
  TUpdateResult extends BaseUpdateResult<TEntity>,
  TUpdateVariables extends OperationVariables,
  TRemoveResult extends BaseRemoveResult,
  TRemoveVariables extends OperationVariables,
  TLinkResult extends BaseLinkResult = undefined,
  TLinkVariables extends OperationVariables = object,
  TListEntities extends BaseEntity = TEntity,
> = ReturnType<
  typeof useApi<
    TEntity,
    TEntityListKey,
    TListResult,
    TListVariables,
    TGetByIdResult,
    TGetByIdVariables,
    TCreateResult,
    TCreateVariables,
    TUpdateResult,
    TUpdateVariables,
    TRemoveResult,
    TRemoveVariables,
    TLinkResult,
    TLinkVariables,
    TListEntities
  >
>

export function useApi<
  TEntity extends BaseEntity,
  TEntityListKey extends keyof QueryRoot,
  TListResult extends BaseListResult<TEntity, TEntityListKey, TListEntities>,
  TListVariables extends OperationVariables,
  TGetByIdResult extends BaseGetByIdResult<TEntity>,
  TGetByIdVariables extends OperationVariables,
  TCreateResult extends BaseCreateResult<TEntity>,
  TCreateVariables extends OperationVariables,
  TUpdateResult extends BaseUpdateResult<TEntity>,
  TUpdateVariables extends OperationVariables,
  TRemoveResult extends BaseRemoveResult,
  TRemoveVariables extends OperationVariables,
  TLinkResult extends BaseLinkResult = undefined,
  TLinkVariables extends OperationVariables = object,
  TListEntities extends BaseEntity = TEntity,
>({
  typename,
  operations,
  resultMap,
  mapRemovedIds,
  listQueryVariables,
  listQueryOptions = ref({}),
  mutationVariables,
  getByIdAdditionalVariables,
  handleAdditionalCacheRemoveUpdates = null,
  beforeRefetch = () => {},
  buildCtxFromResultId = buildNonAdminContext<TListVariables>,
}: ApiParams<
  TEntity,
  TEntityListKey,
  TListResult,
  TListVariables,
  TGetByIdResult,
  TGetByIdVariables,
  TCreateResult,
  TCreateVariables,
  TUpdateResult,
  TUpdateVariables,
  TRemoveResult,
  TRemoveVariables,
  TLinkResult,
  TLinkVariables,
  TListEntities
>) {
  const {
    result: listResult = ref<TListResult>(),
    loading: listLoading = ref(false),
    error = ref(),
    refetch = () => {},
    onResult = () => {},
  } = operations.list
    ? useQuery<TListResult, TListVariables>(operations.list, listQueryVariables, listQueryOptions)
    : {}

  const whenListResultAvailable = useWhenResult(listResult, true)

  const { mutate: createEntity = undefined, loading: createLoading = ref(false) } =
    operations.create
      ? useMutation<TCreateResult, TCreateVariables>(operations.create, {
          errorPolicy: "all",
          update: prepareListCacheReducer(
            (cachedQuery, data) => {
              if (!resultMap.getCreated || !resultMap.getList) return cachedQuery

              const createdItem = resultMap.getCreated(data)
              const list = resultMap.getList(cachedQuery)
              list.items.push(createdItem)
              if (list.total) list.total += 1
              return cachedQuery
            },
            (data) =>
              resultMap.getCreated ? buildCtxFromResultId(resultMap.getCreated(data).id) : undefined
          ),
        })
      : {}

  const { mutate: updateEntity = undefined, loading: updateLoading = ref(false) } =
    operations.update
      ? useMutation<TUpdateResult, TUpdateVariables>(operations.update, {
          errorPolicy: "all",
          update: prepareListCacheReducer(
            (cachedQuery, data) => {
              if (!resultMap.getUpdated || !resultMap.getList) return cachedQuery

              const updatedItem = resultMap.getUpdated(data)
              const list = resultMap.getList(cachedQuery)
              const itemId = list.items.findIndex((item) => item.id === updatedItem.id)
              if (itemId === -1) list.items.push(updatedItem)
              else list.items.splice(itemId, 1, updatedItem)
              return cachedQuery
            },
            (data) =>
              resultMap.getUpdated ? buildCtxFromResultId(resultMap.getUpdated(data).id) : undefined
          ),
        })
      : {}

  const { mutate: linkEntities = undefined, loading: linkLoading = ref(false) } = operations.link
    ? useMutation<TLinkResult, TLinkVariables>(operations.link)
    : {}

  const removeReducers: MutationUpdaterFunction<
    TRemoveResult,
    TRemoveVariables,
    DefaultContext,
    ApolloCache<unknown>
  >[] = []

  function addRemoveReducer(reducer: GetRemoveReducer<TRemoveResult, TRemoveVariables>) {
    removeReducers.push(reducer(mapRemovedIds))
  }

  const { mutate: removeEntities = undefined, loading: removeLoading = ref(false) } =
    operations.remove
      ? useMutation<TRemoveResult, TRemoveVariables>(operations.remove, {
          update: (cache, result, options) => {
            for (const reducer of removeReducers) reducer(cache, result, options)

            handleAdditionalCacheRemoveUpdates?.(cache, result, options)
            cache.gc()
          },
        })
      : {}

  const loading = computed(
    () =>
      listLoading.value ||
      createLoading.value ||
      removeLoading.value ||
      linkLoading.value ||
      updateLoading.value
  )

  function getById(variables: MaybeRef<TGetByIdVariables | undefined>) {
    if (!operations.getById) return undefined

    const refVariables = toRef(variables)
    const additionalVariables = toRef(getByIdAdditionalVariables)

    return useQuery<TGetByIdResult>(
      operations.getById,
      computed(() => ({
        ...(refVariables.value ? refVariables.value : {}),
        ...(additionalVariables.value ? additionalVariables.value : {}),
      })),
      computed(() => ({ enabled: !!refVariables.value }))
    )
  }

  async function create(
    payload: TCreateVariables,
    options?: MutationOptions
  ): MutateResult<TCreateResult> {
    if (!createEntity) return null
    const refVariables = toRef(mutationVariables)
    const result = await createEntity({ ...payload, ...refVariables.value }, options)
    if (handleMutationErrors(result)) return null
    return result
  }

  async function update(
    payload: TUpdateVariables,
    options?: MutationOptions
  ): MutateResult<TUpdateResult> {
    if (!updateEntity) return null
    const refVariables = toRef(mutationVariables)
    const result = await updateEntity(
      {
        ...payload,
        ...(refVariables.value ? refVariables.value : {}),
      },
      options
    )
    if (handleMutationErrors(result)) return null
    return result
  }

  async function link(
    payload: TLinkVariables,
    options?: MutationOptions
  ): MutateResult<TLinkResult> {
    if (!linkEntities) return null

    const result = await linkEntities(payload, options)
    if (handleMutationErrors(result)) return null
    return result
  }

  async function remove(
    payload: TRemoveVariables,
    options?: MutationOptions
  ): MutateResult<TRemoveResult> {
    if (!removeEntities) return null

    const result = await removeEntities(payload, options)
    if (handleMutationErrors(result)) return null
    return result
  }

  function prepareListCacheReducer<
    TResult,
    TVariables extends OperationVariables = OperationVariables,
  >(
    reduce: ListCacheReducer<
      TEntity,
      TEntityListKey,
      TListResult,
      TResult,
      TVariables,
      TListEntities
    >,
    contextVariables?: ListCacheReducerContext<TResult, TListVariables>
  ): MutationUpdaterFunction<TResult, TVariables, DefaultContext, ApolloCache<unknown>> {
    return (cache, { data }, { variables }) => {
      if (!data || !variables || !operations.list) return

      const query = unref(operations.list)
      const queryVariables = unref(listQueryVariables)
      const ctxVariables = contextVariables?.(data as TResult)

      const targetQueryVariables = overrideValuesFromObject(queryVariables, {
        ...(ctxVariables ? ctxVariables : {}),
      } as TListVariables & TVariables)

      cache.updateQuery(
        {
          query,
          variables: targetQueryVariables,
        },
        (cachedData) => {
          if (!cachedData) return
          return {
            ...reduce(structuredClone(cachedData) as TListResult, data as TResult, variables),
          } as Unmasked<TListResult>
        }
      )
    }
  }

  const getListRemoveReducer =
    <
      TReducerRemoveResult extends Record<string, number>,
      TReducerRemoveVariables extends OperationVariables = OperationVariables,
    >(
      modify: (list: EntityList<TEntity>, removedIds: string[]) => void
    ) =>
    (mapRemovedIds: RemovedIdsMapper<TReducerRemoveVariables>) => {
      return prepareListCacheReducer(
        (cachedQuery, _data: TReducerRemoveResult, variables: TReducerRemoveVariables) => {
          if (!resultMap.getList) return cachedQuery

          const list = resultMap.getList(cachedQuery)
          modify(list, mapRemovedIds(variables))
          return cachedQuery
        }
      )
    }

  const getListFilterRemoveReducer = <
    TReducerRemoveResult extends Record<string, number>,
    TReducerRemoveVariables extends OperationVariables = OperationVariables,
  >(
    filterRemovedIds: (removedIds: string[]) => (item: TEntity) => boolean
  ) =>
    getListRemoveReducer<TReducerRemoveResult, TReducerRemoveVariables>((list, removedIds) => {
      list.items = list.items.filter(filterRemovedIds(removedIds))
      if (list.total) list.total -= removedIds.length
    })

  async function refetchQuery() {
    if (beforeRefetch) await beforeRefetch()
    await refetch()
  }

  return reactive({
    typename,
    listResult,
    loading,
    listLoading,
    error,
    refetch: refetchQuery,
    onResult,
    whenListResultAvailable,

    getById: operations.getById ? getById : undefined,
    create,
    update,
    remove,
    link: operations.link ? link : undefined,

    resultMap,
    prepareListCacheReducer,
    getListRemoveReducer,
    getListFilterRemoveReducer,
    addRemoveReducer,
  })
}
